import { ExternalLink } from "@/components/ExternalLink";
import i18n from "@/i18n";
import { formatNumber } from "@/features/Table/utils";
import { parseDate } from "@/lib/utils";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";

export type K2Transaction = {
  id: number;
  date: Date;
  k2_portfolio_client?: string;
  k2_portfolio_id?: number;
  k2_portfolio_name?: string;
  k2_static_datum_id?: number;
  k2_static_datum_name?: string;
  k2_portfolio_reporting_currency?: string;
  quantity?: number;
  market_value_ptf_ccy?: number;
  ptf_currency?: string;
  market_value_local_ccy?: number;
  local_currency?: string;
  category?: string;
  transfer?: string;
  description?: string;
  fees?: number;
  price_local_ccy?: number;
  tax?: number;
  custodian_broker_fee?: number;
  broker_fee?: number;
  other_fee?: number;
  fee_currency?: string;
  fee_type?: string;
};

const tableName = "k2_transactions";
const t = (key: string, defaultValue: string) =>
  i18n.t(`${tableName}.columns.${key}`, { ns: "tables", defaultValue });

export const porfolios_columns: ColumnDef<K2Transaction>[] = [
  {
    accessorKey: "id",
    header: t("id", "ID"),
  },
  {
    accessorKey: "date",
    header: t("date", "Date"),
    cell: ({ row }) => {
      return (
        <div className="whitespace-nowrap">
          {parseDate(String(row.getValue("date")))}
        </div>
      );
    },
  },
  {
    accessorKey: "k2_portfolio_client",
    header: "Client",
  },
  {
    accessorKey: "k2_portfolio_name",
    header: t("k2_portfolio_name", "Account"),
    cell: ({ row }) => {
      return (
        <ExternalLink
          href={toWorkspacePath(
            `/admin/k2/k2_portfolios/${row.original.k2_portfolio_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.getValue("k2_portfolio_name")}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: "k2_static_datum_name",
    header: t("k2_static_datum_name", "Asset"),
    cell: ({ row }) => {
      return (
        <ExternalLink
          href={toWorkspacePath(
            `/admin/k2/k2_static_data/${row.original.k2_static_datum_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.getValue("k2_static_datum_name")}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: "quantity",
    header: () => <div className="text-right">{t("quantity", "Quantity")}</div>,
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue("quantity"))}
        </div>
      );
    },
  },
  {
    accessorKey: "market_value_ptf_ccy",
    header: () => (
      <div className="text-right">
        {t("market_value_ptf_ccy", "Market value (reporting ccy)")}
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue("market_value_ptf_ccy"))}
        </div>
      );
    },
  },
  {
    header: t(
      "k2_portfolio_reporting_currency",
      "Portfolio reporting currency",
    ),
    accessorKey: "k2_portfolio_reporting_currency",
  },
  {
    accessorKey: "market_value_local_ccy",
    header: () => (
      <div className="text-right">
        {t("market_value_local_ccy", "Market value local ccy")}
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue("market_value_local_ccy"))}
        </div>
      );
    },
  },
  {
    accessorKey: "local_currency",
    header: t("local_currency", "Local currency"),
  },
  {
    accessorKey: "fees",
    header: () => <div className="text-right">{t("fees", "Fees")}</div>,
    cell: ({ row }) => {
      return (
        <div className="text-right">{formatNumber(row.getValue("fees"))}</div>
      );
    },
  },
  {
    accessorKey: "fee_currency",
    header: t("fee_currency", "Fee currency"),
  },
  {
    accessorKey: "description",
    header: t("description", "Description"),
  },
  {
    accessorKey: "category",
    header: t("category", "Movement type"),
  },
  {
    accessorKey: "transfer",
    header: t("transfer", "Transfer type"),
    cell: ({ row }) => {
      return <div className="text-left">{row.getValue("transfer")}</div>;
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue("id")}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      );
    },
  },
];

// export const porfolios_audit_columns = getAuditColumns<K2Transaction>(porfolios_columns)
